import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../reducers/rootReducer';
import { featureFlagsSelectors } from '../../reducers/featureFlagSlice';
import { PERSISTENT_SNACKBAR } from '../constants';
import {
  addSnackbar,
  hydrateClosedPersistentSnackbars,
  hydrateClosedTemporarySnackbars,
  Snackbar,
} from '../../reducers/snackbarSlice';

function useSnackbarFromFeatureFlag() {
  const dispatch = useDispatch();
  const { currentUser } = useAppSelector((state) => state.userSession);

  const isActivePersistentSnackbar = useAppSelector((state) =>
    featureFlagsSelectors.isFlagActive(state, PERSISTENT_SNACKBAR),
  );
  const isActiveTemporarySnackbar = useAppSelector(
    (state) =>
      state.featureFlag.availableFeatureFlags?.temporary_snackbar?.active,
  );
  const availableFeatureFlags = useAppSelector(
    (state) => state.featureFlag.availableFeatureFlags,
  );

  const rawPersistentMsg =
    availableFeatureFlags?.persistent_snackbar?.description;

  const persistentSnackbarMessages = rawPersistentMsg?.includes('^')
    ? rawPersistentMsg.split('^')
    : [rawPersistentMsg];

  const rawTemporaryMsg =
    availableFeatureFlags?.temporary_snackbar?.description;

  const temporarySnackbarMessages = rawTemporaryMsg?.includes('^')
    ? rawTemporaryMsg.split('^')
    : [rawTemporaryMsg];

  useEffect(() => {
    const closedTemporarySnackbars =
      localStorage.getObject?.('closedTemporarySnackbars') || {};
    dispatch(
      hydrateClosedTemporarySnackbars(Object.values(closedTemporarySnackbars)),
    );

    const closedPersistentSnackbars =
      sessionStorage.getObject?.('closedPersistentSnackbars') || {};
    dispatch(
      hydrateClosedPersistentSnackbars(
        Object.values(closedPersistentSnackbars),
      ),
    );
  }, []);

  useEffect(() => {
    if (currentUser.includes('ltda-test-account.com')) {
      return;
    }

    if (isActiveTemporarySnackbar === false) {
      localStorage.removeItem('closedTemporarySnackbars');
      dispatch(hydrateClosedTemporarySnackbars([]));
    }

    temporarySnackbarMessages.forEach((m) => {
      displaySnackbar({
        isActive: isActiveTemporarySnackbar,
        message: m,
        persistencyType: 'temporary',
        currentUser,
        dispatch,
      });
    });
  }, [isActiveTemporarySnackbar, temporarySnackbarMessages, currentUser]);

  useEffect(() => {
    if (currentUser.includes('ltda-test-account.com')) {
      return;
    }

    persistentSnackbarMessages.forEach((m) => {
      displaySnackbar({
        isActive: isActivePersistentSnackbar,
        message: m,
        persistencyType: 'persistent',
        currentUser,
        dispatch,
      });
    });
  }, [isActivePersistentSnackbar, persistentSnackbarMessages, currentUser]);
}

function displaySnackbar({
  isActive,
  message,
  persistencyType,
  currentUser,
  dispatch,
}: {
  isActive: boolean;
  message: string;
  persistencyType: 'temporary' | 'persistent';
  currentUser: string;
  dispatch: any;
}) {
  if (isActive && message && currentUser) {
    const snackbar: Snackbar = {
      id: `${persistencyType}-snackbar-${hashString(message)}`,
      message,
      type: 'info',
      isOpen: true,
      persistencyType,
    };
    dispatch(addSnackbar({ snackbar, currentUser }));
  }
}

function hashString(str: string) {
  let hash = 0;
  let i;
  let chr;
  if (str.length === 0) return hash.toString();
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return Math.abs(hash).toString();
}

export default useSnackbarFromFeatureFlag;
